import { useState, useEffect } from "react";
import { ChevronRightIcon } from "@heroicons/react/outline";

import { CopyButton } from "../copy-button";
import FileDisplay, { DetectedFile } from "../FileDisplay";
import { useFileContent } from "../FileDisplay/hooks";
import { FileCollection } from "../FileDisplay/FileDisplayContent/FileCollection";
import { DetectedFileItem } from "../FileDisplay/utils";

import { useUserToken } from "@/context/auth-context";
import { useRequestLogInputVariables } from "@/queries/requests";
import LoadingSpinner from "@/components/LoadingSpinner";

interface InputVariablePopoverItemProps {
  itemKey: string;
  value: string | boolean | object;
  requestId?: string | number | null;
}

const VariableContentDisplay: React.FC<{
  isProcessingFile: boolean;
  isFileContent: boolean;
  fileItems: { data?: string; fileType?: string | null }[] | null;
  isFileCollectionPopoverOpen: boolean;
  setIsFileCollectionPopoverOpen: (open: boolean) => void;
  isExpanded: boolean;
  toggleExpand: () => void;
  getDisplayValue: (val: boolean | object | string) => string;
  value: string | boolean | object;
}> = ({
  isProcessingFile,
  isFileContent,
  fileItems,
  isFileCollectionPopoverOpen,
  setIsFileCollectionPopoverOpen,
  isExpanded,
  toggleExpand,
  getDisplayValue,
  value,
}) => {
  if (isProcessingFile) {
    return (
      <div className="ml-2 w-full animate-pulse">
        <div className="mb-2 h-4 w-3/4 rounded bg-gray-200" />
        <div className="h-3 w-1/2 rounded bg-gray-200" />
      </div>
    );
  }

  if (isFileContent) {
    return (
      <div className="ml-2 w-full">
        {fileItems && fileItems.length > 0 ? (
          <FileCollection
            isPopoverOpen={isFileCollectionPopoverOpen}
            setIsPopoverOpen={setIsFileCollectionPopoverOpen}
            fileCollectionCount={fileItems?.length || 0}
            detectedFileValues={fileItems?.map(item => item.data || "") || []}
          />
        ) : (
          <FileDisplay
            data={fileItems?.[0]?.data || ""}
            className="max-w-full"
            maxWidth="100%"
            maxHeight="200px"
            detectedFile={fileItems?.[0]?.fileType as DetectedFile | null}
          />
        )}
      </div>
    );
  }

  return (
    <>
      <button onClick={toggleExpand} className="ml-2 focus:outline-none">
        <ChevronRightIcon
          className={`h-4 w-4 transform transition-transform ${isExpanded ? "rotate-90" : ""}`}
        />
      </button>
      <div className="ml-2 flex flex-1 items-start gap-2 overflow-hidden">
        <pre
          className={`flex-1 text-xs text-gray-500 ${
            isExpanded
              ? "whitespace-pre-wrap break-words"
              : "overflow-hidden text-ellipsis whitespace-nowrap"
          }`}
        >
          {getDisplayValue(value)}
        </pre>
        <CopyButton
          text={typeof value !== "string" ? JSON.stringify(value) : value}
          iconSize="h-4 w-4"
        />
      </div>
    </>
  );
};

export const InputVariablePopoverItem = ({
  itemKey,
  value,
  requestId,
}: InputVariablePopoverItemProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isFileCollectionPopoverOpen, setIsFileCollectionPopoverOpen] = useState(false);
  const [hasStartedFetch, setHasStartedFetch] = useState(false);

  const userToken = useUserToken();

  // Convert requestId to number if it's a string
  const numericRequestId = requestId ? Number(requestId) : null;

  // Start fetching when expanded for the first time
  useEffect(() => {
    if (isExpanded && !hasStartedFetch && numericRequestId && userToken) {
      setHasStartedFetch(true);
    }
  }, [isExpanded, hasStartedFetch, numericRequestId, userToken]);

  // Fetch full input variables - keep stable parameters once started
  const {
    data: fullInputVariables,
    isLoading,
    isSuccess,
  } = useRequestLogInputVariables(
    userToken,
    hasStartedFetch && numericRequestId ? numericRequestId : null,
  );

  // Determine which value to display: use full data if successfully fetched, otherwise use preview
  const fullValue =
    isSuccess && fullInputVariables?.prompt_input_variables?.[itemKey]
      ? fullInputVariables.prompt_input_variables[itemKey]
      : value;
  const displayValue = isExpanded && isSuccess ? fullValue : value;
  const valueToDetect =
    typeof displayValue !== "string" ? JSON.stringify(displayValue) : displayValue;

  const { fileItems, isFileContent, isProcessingFile } = useFileContent(valueToDetect);

  const getDisplayValue = (val: boolean | object | string): string => {
    if (typeof val === "boolean") {
      return val ? "True" : "False";
    }
    if (typeof val === "object") {
      return JSON.stringify(val, null, 2);
    }
    return val;
  };

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <tr className="hover:bg-gray-100">
      <td className="w-1/3 px-4 py-2 text-gray-600">
        <div className="truncate font-mono text-xs" title={itemKey}>
          {itemKey}
        </div>
      </td>
      <td className="w-2/3 px-4 py-2 text-gray-600">
        <div className="flex items-start justify-between overflow-y-auto">
          <div className="flex w-full items-start">
            {!isFileContent && !isProcessingFile && (
              <CopyButton
                text={
                  typeof displayValue !== "string" ? JSON.stringify(displayValue) : displayValue
                }
                iconSize="h-4 w-4"
              />
            )}
            {/* Show loading only when we're actively fetching */}
            {hasStartedFetch && isLoading ? (
              <div className="ml-2 flex items-center gap-2">
                <LoadingSpinner size={4} />
                <span className="text-xs text-gray-500">Loading full data...</span>
              </div>
            ) : (
              <VariableContentDisplay
                isProcessingFile={isProcessingFile}
                isFileContent={isFileContent}
                // @ts-expect-error - TODO: fix this
                fileItems={fileItems as DetectedFileItem[]}
                isFileCollectionPopoverOpen={isFileCollectionPopoverOpen}
                setIsFileCollectionPopoverOpen={setIsFileCollectionPopoverOpen}
                isExpanded={isExpanded}
                toggleExpand={toggleExpand}
                getDisplayValue={getDisplayValue}
                value={displayValue}
              />
            )}
          </div>
        </div>
      </td>
    </tr>
  );
};
